import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import { useRouter } from 'next/router'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react'

interface SmoothScrollProps {
    children: ReactNode
}

interface ScrollToHashOptions {
    immediate?: boolean
}

const LenisContext = createContext<Lenis | null>(null)

/** The running smooth-scroll instance, or null before mount and for reduced motion. */
export const useLenis = (): Lenis | null => useContext(LenisContext)

/** Scrolls to an in-page anchor ("#programme") with the smooth scroller when it is running. */
export const useScrollToHash = () => {
    const lenis = useLenis()

    return useCallback((hash: string, { immediate = false }: ScrollToHashOptions = {}) => {
        const target = document.querySelector<HTMLElement>(hash)

        if (!target) return

        if (lenis) {
            lenis.start()
            lenis.scrollTo(target, { duration: 1.6, immediate, force: true })
            return
        }

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        target.scrollIntoView({ behavior: immediate || reduced ? 'auto' : 'smooth', block: 'start' })
    }, [lenis])
}

export const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const [lenis, setLenis] = useState<Lenis | null>(null)
    const { events } = useRouter()

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const instance = new Lenis({
            autoRaf: true,
            lerp: 0.09,
            stopInertiaOnNavigate: true
        })

        setLenis(instance)

        return () => {
            instance.destroy()
            setLenis(null)
        }
    }, [])

    // A new page starts at the top, without the previous page's inertia.
    useEffect(() => {
        if (!lenis) return

        const onRouteDone = (url: string) => {
            lenis.start()

            if (url.includes('#')) return

            lenis.scrollTo(0, { immediate: true, force: true })
            lenis.resize()
        }

        events.on('routeChangeComplete', onRouteDone)

        return () => events.off('routeChangeComplete', onRouteDone)
    }, [lenis, events])

    return (
        <LenisContext.Provider value={lenis}>
            {children}
        </LenisContext.Provider>
    )
}
